
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { useDashboardStats } from '@/hooks/dashboard/useDashboardStats';
import { Users, CreditCard, AlertTriangle, CheckCircle } from 'lucide-react';
import { Badge } from '@/components/ui/badge';

const DashboardStats: React.FC = () => {
  const { t } = useTranslation();
  const { data: stats, isLoading } = useDashboardStats();

  if (isLoading || !stats) {
    return (
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        {[...Array(4)].map((_, i) => (
          <Card key={i} className="animate-pulse">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <div className="h-4 bg-gray-200 rounded w-1/2"></div>
              <div className="h-4 w-4 bg-gray-200 rounded"></div>
            </CardHeader>
            <CardContent>
              <div className="h-8 bg-gray-200 rounded w-1/3 mb-1"></div>
              <div className="h-3 bg-gray-200 rounded w-2/3"></div>
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  const statCards = [
    {
      title: t('dashboard.stats.totalClients'),
      value: stats.totalClients,
      description: t('dashboard.stats.activeClients'),
      icon: Users,
      color: 'text-blue-600',
    },
    {
      title: t('dashboard.stats.unpaidClients'),
      value: stats.unpaidClients,
      description: t('dashboard.stats.needsPayment'),
      icon: CreditCard,
      color: 'text-red-600',
    },
    {
      title: t('dashboard.stats.lowSessions'),
      value: stats.lowSessionsClients,
      description: t('dashboard.stats.sessionAlert'),
      icon: AlertTriangle,
      color: 'text-orange-600',
    },
    {
      title: t('dashboard.stats.paidClients'),
      value: stats.paidClients,
      description: t('dashboard.stats.upToDate'),
      icon: CheckCircle,
      color: 'text-green-600',
    },
  ];

  return (
    <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
      {statCards.map((stat, index) => (
        <Card key={index}>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">{stat.title}</CardTitle>
            <stat.icon className={`h-4 w-4 ${stat.color}`} />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stat.value}</div>
            <p className="text-xs text-muted-foreground">{stat.description}</p>
            {stat.value > 0 && (index === 1 || index === 2) && (
              <Badge variant="destructive" className="mt-2">
                {t('dashboard.requiresAttention')}
              </Badge>
            )}
          </CardContent>
        </Card>
      ))}
    </div>
  );
};

export default DashboardStats;
